import React from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { loginRequest } from '../../config/authConfig';
import { appConfig } from '../../config/appConfig';
import { LogIn, AlertCircle } from 'lucide-react';

interface AuthGuardProps {
  children: React.ReactNode;
  message?: string;
}

/**
 * AuthGuard component - protects content requiring authentication
 * If authentication is disabled in config, renders children directly
 * If authentication is enabled but user is not authenticated, shows sign-in prompt
 */
export const AuthGuard: React.FC<AuthGuardProps> = ({ 
  children, 
  message = 'You need to sign in to access this feature.' 
}) => {
  const isAuthenticated = useIsAuthenticated();
  const { instance } = useMsal();

  // If authentication is disabled, allow access
  if (!appConfig.enableAuthentication) {
    return <>{children}</>;
  }

  // If user is authenticated, render children
  if (isAuthenticated) {
    return <>{children}</>;
  }

  // User needs to sign in
  const handleLogin = async () => {
    try {
      await instance.loginRedirect(loginRequest);
    } catch (error) {
      console.error('Login error:', error);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center p-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
      <AlertCircle className="w-12 h-12 text-gray-400 mb-4" />
      <h3 className="text-lg font-semibold text-gray-900 mb-2">Authentication Required</h3>
      <p className="text-gray-600 text-center mb-6 max-w-md">{message}</p>
      <button
        onClick={handleLogin}
        className="flex items-center gap-2 px-6 py-3 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
      >
        <LogIn size={18} />
        Sign In to Continue
      </button>
    </div>
  );
};
